import { MAX_ERROR_LOGS } from '../config/constants';

/**
 * Centralized error logging and tracking service
 * Replaces scattered console.error calls throughout the application
 */

type ErrorSeverity = 'error' | 'warning' | 'info';

interface ErrorContext {
    userId?: string;
    action?: string;
    component?: string;
    metadata?: Record<string, unknown>;
}

interface ErrorLogEntry {
    timestamp: string;
    message: string;
    severity: ErrorSeverity;
    error?: Error;
    context?: ErrorContext;
    stack?: string;
}

class ErrorService {
    private logs: ErrorLogEntry[] = [];
    private readonly maxLogs = MAX_ERROR_LOGS;
    private sentryInitialized = false;

    /**
     * Initialize error tracking service (e.g., Sentry)
     * Call this once during app initialization
     */
    initialize(config?: { dsn?: string; environment?: string }): void {
        if (config?.dsn && typeof window !== 'undefined') {
            // TODO: Initialize Sentry when ready
            // Sentry.init({
            //     dsn: config.dsn,
            //     environment: config.environment || 'development',
            //     beforeSend(event) {
            //         // Filter out sensitive data
            //         return event;
            //     }
            // });
            this.sentryInitialized = true;
            console.info('[ErrorService] Error tracking initialized');
        }
    }

    /**
     * Log an error with context
     */
    logError(message: string, error?: Error | unknown, context?: ErrorContext): void {
        const entry: ErrorLogEntry = {
            timestamp: new Date().toISOString(),
            message,
            severity: 'error',
            error: error instanceof Error ? error : undefined,
            context,
            stack: error instanceof Error ? error.stack : undefined
        };

        this.addToLogs(entry);

        // Log to console in development
        if (import.meta.env.DEV) {
            console.error(`[Error] ${message}`, {
                error,
                context,
                stack: entry.stack
            });
        }

        // Send to error tracking service in production
        if (this.sentryInitialized && error instanceof Error) {
            // TODO: Send to Sentry
            // Sentry.captureException(error, {
            //     tags: context?.metadata,
            //     contexts: {
            //         action: context?.action,
            //         component: context?.component
            //     }
            // });
        }

        // Emit custom event for UI error display
        this.emitErrorEvent(entry);
    }

    /**
     * Log a warning
     */
    logWarning(message: string, context?: ErrorContext): void {
        const entry: ErrorLogEntry = {
            timestamp: new Date().toISOString(),
            message,
            severity: 'warning',
            context
        };

        this.addToLogs(entry);

        if (import.meta.env.DEV) {
            console.warn(`[Warning] ${message}`, context);
        }
    }

    /**
     * Log info message
     */
    logInfo(message: string, context?: ErrorContext): void {
        const entry: ErrorLogEntry = {
            timestamp: new Date().toISOString(),
            message,
            severity: 'info',
            context
        };

        this.addToLogs(entry);

        if (import.meta.env.DEV) {
            console.info(`[Info] ${message}`, context);
        }
    }

    /**
     * Get recent error logs (for debugging)
     */
    getLogs(): ErrorLogEntry[] {
        return [...this.logs];
    }

    /**
     * Clear error logs
     */
    clearLogs(): void {
        this.logs = [];
    }

    private addToLogs(entry: ErrorLogEntry): void {
        this.logs.push(entry);

        // Keep only the most recent logs
        if (this.logs.length > this.maxLogs) {
            this.logs.shift();
        }
    }

    private emitErrorEvent(entry: ErrorLogEntry): void {
        if (typeof window === 'undefined') return;

        window.dispatchEvent(
            new CustomEvent('app-error', {
                detail: entry
            })
        );
    }
}

// Export singleton instance
export const errorService = new ErrorService();

/**
 * Helper to extract error message from unknown error type
 */
export function getErrorMessage(error: unknown): string {
    if (error instanceof Error) {
        return error.message;
    }
    if (typeof error === 'string') {
        return error;
    }
    try {
        return JSON.stringify(error);
    } catch {
        return 'Unknown error';
    }
}

